/**
 * Element analytics tracking helpers.
 *
 * buildDataAttributes — converts a property object to data-* attributes.
 * buildFullStoryAttributes — adds FullStory-specific schema and element type (swappable).
 * trackElement — generates a complete tracked element with test id, data attributes, and analytics provider attributes.
 */

import slugify from "slugify";

slugify.extend({ _: "-" });

export type PropertyValue = string | number | boolean;

/**
 * Converts property keys to data-* attributes (underscores → hyphens).
 *
 * @example
 * buildDataAttributes({ page_category: "crunch_view", item_order: 3 })
 * // { "data-page-category": "crunch_view", "data-item-order": 3 }
 */
export function buildDataAttributes(properties: Record<string, PropertyValue>): Record<string, PropertyValue> {
  const attrs: Record<string, PropertyValue> = {};

  for (const [key, value] of Object.entries(properties)) {
    attrs[`data-${slugify(key, { lower: true })}`] = value;
  }

  return attrs;
}

/**
 * Generates FullStory-specific tracking attributes.
 *
 * Adds `data-fs-element` (the element type) and `data-fs-properties-schema`
 * (a JSON map of attribute names → FullStory type hints).
 *
 * This method can be swapped for a different analytics provider's implementation.
 */
export function buildFullStoryAttributes(
  type: string,
  dataAttributes: Record<string, PropertyValue>,
): Record<string, PropertyValue> {
  const schema: Record<string, string> = {};

  for (const [key, value] of Object.entries(dataAttributes)) {
    schema[key] = typeof value === "number" ? "real" : typeof value === "boolean" ? "bool" : "str";
  }

  return {
    "data-fs-element": type,
    "data-fs-properties-schema": JSON.stringify(schema),
  };
}

export function buildDataTestId(name: string, type: string): string {
  return slugify(`${name}-${type}`, { lower: true });
}

/**
 * Generates element tracking attributes for analytics and testing.
 *
 * Builds data-* attributes from properties, a data-testid for test selectors,
 * and analytics provider attributes (currently FullStory).
 *
 * @example
 * // Simple element
 * <Button {...trackElement("Button", "Export Document")}>Export</Button>
 *
 * // With additional properties
 * <Button {...trackElement("Button", "Export Document", { page_category: "crunch_view" })}>
 */
export const ELEMENT_TYPE = {
  Button: "Button",
  DropdownItem: "Dropdown Item",
  Tab: "Tab",
  Link: "Link",
  PanelClose: "Panel Close",
  PanelOpen: "Panel Open",
  Input: "Input",
} as const;

export type ElementType = (typeof ELEMENT_TYPE)[keyof typeof ELEMENT_TYPE];
export type TrackingProps = Record<string, PropertyValue>;

export function trackElement(
  type: ElementType,
  name: string,
  properties?: Record<string, PropertyValue>,
): TrackingProps {
  const dataAttrs = buildDataAttributes({ name, ...properties });

  return {
    "data-testid": buildDataTestId(name, type),
    ...dataAttrs,
    ...buildFullStoryAttributes(type, dataAttrs),
  };
}
